import React, { useState, useEffect } from 'react';
import { Button, Table, Input, message, Switch, DatePicker, Dropdown, Menu, Tooltip } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import * as _ from 'lodash';

import { RedirectIcon, FilterIcon } from 'components/Icons';
import { FETCH_WHITELISTED_LINKS } from 'graphql/queries/whitelistedLinkQueries';
import { UPDATE_WHITELISTED_LINK } from 'graphql/mutations/whitelistedLinkMutations';
import utils from 'utils/utils';
import SpamLinksProposals from './SpamLinksProposals';
import { makeNavigableLink } from 'utils/spamUrl';

import './SpamLinks.scss';

const { RangePicker } = DatePicker;
const limit = 30;

const SpamLinks = () => {
  const endDate = moment().toISOString();
  const initialFilters = {
    search: '',
    blocked: 'all',
    startDate: moment(endDate).subtract(1, 'month'),
    endDate: moment(endDate),
  };

  const [links, setLinks] = useState([]);

  const [search, setSearch] = useState(initialFilters.search);
  const [blocked, setBlocked] = useState(initialFilters.blocked);
  const [selectedDate, setSelectedDate] = useState([
    initialFilters.startDate,
    initialFilters.endDate,
  ]);

  const [modal, setModal] = useState(null);
  const [cursorPaging, setCursorPaging] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const getBlocked = (blocked) => {
    if (blocked === 'blocked') {
      return { blocked: true };
    }
    if (blocked === 'whitelisted') {
      return { blocked: false };
    }
    return { blocked: null };
  };

  const getSelectedDate = (selectedDate) => {
    if (selectedDate.length === 2) {
      return [selectedDate[0].toISOString(), selectedDate[1].toISOString()];
    }
    return [initialFilters.startDate.toISOString(), initialFilters.endDate.toISOString()];
  };

  // -------------- fetch, refetch whitelisted links ---------------
  const [fetchWhitelistedLinks, { refetch, fetchMore }] = useLazyQuery(FETCH_WHITELISTED_LINKS, {
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchWhitelistedLinks }) => {
      processFetchedData(fetchWhitelistedLinks);
    },
  });

  const processFetchedData = (data) => {
    const { pagingMetadata, links } = data;
    const _links = links.map((l) => ({
      _id: l._id,
      url: l.url,
      blocked: l.blocked,
      createdAt: l.createdAt,
      updatedAt: l.updatedAt,
    }));
    setLinks([..._links]);
    setCursorPaging(pagingMetadata);
    setIsFetching(false);
  };

  const buildQueryOptions = (options, allowCursorPaging = false) => {
    return {
      variables: {
        search: options?.search || search,
        ...getBlocked(options?.blocked ?? blocked),
        selectedDate: getSelectedDate(options?.selectedDate ?? selectedDate),
        limit: limit,
        ...(allowCursorPaging && {
          cursorPaging: options?.cursor ?? cursorPaging?.cursor,
        }),
      },
    };
  };

  const refetchLinks = async (options = {}, allowCursorPaging = false) => {
    setIsFetching(true);

    const { variables } = buildQueryOptions(options, allowCursorPaging);
    const { data } = await refetch(variables);
    processFetchedData(data?.fetchWhitelistedLinks);
  };

  const loadMore = async () => {
    // load more with button click
    setIsFetching(true);
    const { data } = await fetchMore(buildQueryOptions({}, true));
    processFetchedData(data?.fetchAdminUsers);
  };

  useEffect(() => {
    // initial load
    setIsFetching(true);
    fetchWhitelistedLinks(buildQueryOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // load data when filter changes

    if (isFetching) {
      return;
    }
    refetchLinks({
      blocked,
      selectedDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, selectedDate]);

  const debounceRefetch = _.debounce((options) => {
    refetchLinks({ ...options });
  }, 2000);

  const onSearch = (e) => {
    const { value } = e.target;
    setSearch(value);

    debounceRefetch({
      search: value,
      blocked,
      selectedDate,
    });
  };
  // -------------- END of fetch, refetch whitelisted links ---------------

  const [updateWhitelistedLink] = useMutation(UPDATE_WHITELISTED_LINK, {
    //onCompleted: () => message.success('Job mail added to queue successfully'),
    onError: () => message.error(`Failed to update`),
  });

  const handleReset = () => {
    setSelectedDate([initialFilters.startDate, initialFilters.endDate]);
    setSearch(initialFilters.search);
    setBlocked(initialFilters.blocked);
  };

  const onChange = (id, key, value) => {
    setLinks((prev) => {
      const newData = [...prev];
      const index = newData.findIndex((d) => d._id === id);

      if (index > -1) {
        newData[index][key] = value;
      }

      updateWhitelistedLink({
        variables: {
          _id: id,
          [key]: value,
        },
      });

      return newData;
    });
  };

  const linksColumn = [
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (data) => <>{moment(data).format('YYYY-MM-DD')}</>,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url) => (
        <a href={makeNavigableLink(url)} target="_blank" rel="noreferrer">
          {utils.cutLongText(url)} &nbsp;
          <RedirectIcon />
        </a>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'blocked',
      key: 'blocked',
      render: (blocked, data) => (
        <>
          <Switch
            checkedChildren="Whitelisted"
            unCheckedChildren="Blocked"
            checked={!blocked}
            onChange={() => onChange(data._id, 'blocked', !blocked)}
          />
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (data) => (
        <Button onClick={() => setModal({ spamUrl: data.url })}>View proposals</Button>
      ),
    },
  ];

  const menu = (
    <Menu
      onClick={({ key }) => setBlocked(key)}
      defaultSelectedKeys={[initialFilters.blocked]}
      selectedKeys={blocked}>
      <Menu.Item key="all">All</Menu.Item>
      <Menu.Item key="blocked">Blocked</Menu.Item>
      <Menu.Item key="whitelisted">White listed</Menu.Item>
    </Menu>
  );

  return (
    <div className="admin-wrapper">
      <div className="admin-table-wrapper">
        <div className="table-action">
          <h3>Spam links</h3>
          <div className="actions">
            <RangePicker
              value={[...selectedDate]}
              onChange={(values) => {
                if (values.length === 2) {
                  setSelectedDate([...values]);
                }
              }}
            />

            <Input
              placeholder="Search with url..."
              value={search}
              onChange={onSearch}
              className="search-input"
            />

            <Dropdown overlay={menu}>
              <Tooltip title="Status Filter">
                <FilterIcon />
              </Tooltip>
            </Dropdown>

            <Button className="medium-btn secondary-btn" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        <Table
          loading={isFetching}
          dataSource={links}
          columns={linksColumn}
          rowKey={'_id'}
          pagination={false}
          footer={() => (
            <div>
              {cursorPaging?.hasNext && (
                <Button
                  loading={isFetching}
                  disabled={isFetching}
                  className="medium-btn white-btn"
                  onClick={loadMore}>
                  Load more
                </Button>
              )}
            </div>
          )}
        />
      </div>

      {modal?.spamUrl && (
        <SpamLinksProposals spamUrl={modal.spamUrl} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

export default SpamLinks;
